import { useContext, useEffect } from "react"

import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom"

import { ToastContainer } from 'react-toastify'

import About from "./pages/About"
import Home from "./pages/Home"
import Login from "./pages/Login"
import SignUp from "./pages/SignUp"
import Session from "./pages/Session"
import CreateQuestionPage from "./pages/CreateQuestionPage"
import LabelingPage from "./pages/LabelingPage"
import WorkspacePage from "./pages/WorkspacePage"
import StudentDashboardPage from "./pages/StudentDashboardPage"
import StudentRevisitPage from "./pages/StudentRevisitPage"
import DatasetPage from "./component/dataset/DatasetPage"
import ChiPage from "./pages/ChiPage"
import StudyPage from "./study/StudyPage"
import StudyEntry from "./study/StudyEntry"
import StudyGate from "./study/StudyGate"
import StudyReplayPage from "./study/replay/StudyReplayPage"
import ReasoningGapAssign from "./component/reasoningGap/ReasoningGapAssign"

import { AuthContext } from "./context/AuthContext"
import { DashboardProviders } from "./context/DashboardProviders"
import { rememberReturnPath, rememberedReturnPath, sessionPathIn } from "./tool/returnPath"

import "./css/home.scss"
import "./css/login.scss"
import "./css/session.scss"
import 'react-toastify/dist/ReactToastify.css'

// Whether someone has been signed in in this tab (ProtectedRoute notes it): on this page load,
// or before a reload - a student's Sign Out (HeaderBanner) reloads the page they were on, and
// the guard then runs on a fresh page load. So it is also kept in sessionStorage, which lasts
// as long as the tab. The guard's redirect after that is a sign-out, or a sign-in that ended,
// not a link opened signed out.
const SIGNED_IN_KEY = 'vzSignedInHere'
let hadSignedInUser = false
const noteSignedIn = () => {
  hadSignedInUser = true
  try {
    sessionStorage.setItem(SIGNED_IN_KEY, '1')
  } catch (error) {
    // No storage (a private window): this page load still knows.
  }
}
const someoneSignedInHere = () => {
  if (hadSignedInUser) return true
  try {
    return sessionStorage.getItem(SIGNED_IN_KEY) === '1'
  } catch (error) {
    return false
  }
}

// The sign-in guard's redirect. For a link opened signed out, it also keeps the page asked for
// in this browser for a few hours (tool/returnPath.js): a sign-in that does not start from
// this redirect - vizpi.org or /login typed, a new tab - has no `from`, and still comes back
// to it. Not after a sign-out: the next person to sign in on this browser would land on the
// page that was left (the experiment entry screen's Sign in again keeps its link itself).
const RedirectToLogin = ({ from }) => {
  useEffect(() => {
    if (!someoneSignedInHere()) rememberReturnPath(from.pathname)
  }, [from.pathname])
  return <Navigate to="/login" state={{ from }} replace />
}

// Declared here, not inside App: a component defined in App's body is a new component type
// on every App render, and App re-rendered on every currentUser change. React then unmounted
// and remounted the whole protected page each time the signed-in user changed - e.g. right
// after Home saves the session a student opened - repeating every request the page makes.
const ProtectedRoute = ({ children }) => {
  const { currentUser, currentUserLoading } = useContext(AuthContext)
  const location = useLocation()

  useEffect(() => {
    if (currentUser) noteSignedIn()
  }, [currentUser])

  if (currentUserLoading) {
    return <div>Loading...</div>
  }

  if (!currentUser) {
    // Redirect to login and save the intended destination
    return <RedirectToLogin from={location} />
  }

  return children
}

// /workspace without an id, and any path the app has no page for, rendered a blank page: they
// go to the session whose number the address carries (a mistyped link, sessionPathIn), else
// the page the sign-in guard last kept (tool/returnPath.js) - a student's exercise link - or
// the session list. Only read here: a sign-in is what uses the kept page up.
const ReturnToKeptPage = () => {
  const { pathname } = useLocation()
  const typed = sessionPathIn(pathname)
  const kept = rememberedReturnPath()
  return <Navigate to={typed || (kept && kept !== pathname ? kept : '/session')} replace />
}

// /instructor-dashboard/:id/:sessionId used to be the only place instructors could see
// class analytics/lobby/replay, but nothing in the app ever linked to it - that content
// now lives as a "Session Dashboard" tab on /session/:id itself (GuiExerciseEditor.jsx /
// TabPanels.jsx), so this route just forwards there instead of 404ing a bookmarked link.
const InstructorDashboardRedirect = () => {
  const { id } = useParams()
  return <Navigate to={`/session/${id}`} replace />
}

function App () {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/">
          <Route path="" element={<About />} />
          <Route path="login" element={<Login />} />
          <Route path="about" element={<About />} />
          <Route path="signup" element={<SignUp />} />
          <Route path="rg/:a/:b" element={<ReasoningGapAssign />} />
          <Route path="rg/:a" element={<ReasoningGapAssign />} />

          <Route path="session" element={
            <ProtectedRoute>
              <DashboardProviders>
                <Session />
              </DashboardProviders>
            </ProtectedRoute>
          } />

          {/* Static path, declared before session/:id. React Router v6 ranks static
              segments above dynamic ones regardless of order, the same way /study/followup
              already coexists with /study/:token below - so "new" can never be read as a
              session id. */}
          <Route path="session/new" element={
            <ProtectedRoute>
              <DashboardProviders>
                <CreateQuestionPage />
              </DashboardProviders>
            </ProtectedRoute>
          } />

          <Route path="session/:id" element={
            <ProtectedRoute>
              <DashboardProviders>
                <Home />
              </DashboardProviders>
            </ProtectedRoute>
          } />
          
          <Route path="labeling" element={
            <ProtectedRoute>
              <LabelingPage />
            </ProtectedRoute>
          } />

           <Route path="workspace/:id" element={
            <ProtectedRoute>
              <WorkspacePage />
            </ProtectedRoute>
          } />
          {/* A workspace link without its id: see ReturnToKeptPage. */}
          <Route path="workspace" element={<ReturnToKeptPage />} />

          {/* Real student accounts only (role 4) - each page redirects otherwise. Not
              wrapped in ProtectedRoute since that only checks currentUser truthiness, not
              role; the guard lives in the pages themselves. */}
          <Route path="my-exercises" element={<StudentDashboardPage />} />
          <Route path="my-exercises/:sessionId" element={<StudentRevisitPage />} />
          <Route path="instructor-dashboard/:id/:sessionId" element={<InstructorDashboardRedirect />} />

          {/* Research dataset dashboard */}
          <Route path="dataset" element={
              <DatasetPage />
          } />

          {/* Cosmos assessment IDE recreation (demo) */}
          <Route path="chi" element={<ChiPage />} />

          {/* CHI study participant flow (server-owned step engine).
              Static segments outrank the dynamic :token, so /study/followup and
              /study/replay are never shadowed by an invite link. */}
          {/* StudyGate, not ProtectedRoute: participants enter by personal invite link,
              so an unauthenticated visitor needs telling that — not a redirect to a
              sign-in page whose guest button would create a second participant record.

              One URL per part. The server still owns which part a participant is in;
              these paths follow it. Bare /study redirects to the right one. */}
          <Route path="study" element={
            <StudyGate><StudyPage mode="auto" /></StudyGate>
          } />
          <Route path="study/screening" element={
            <StudyGate><StudyPage mode="screening" /></StudyGate>
          } />
          <Route path="study/main" element={
            <StudyGate><StudyPage mode="main" /></StudyGate>
          } />
          <Route path="study/followup" element={
            <StudyGate><StudyPage mode="followup" /></StudyGate>
          } />
          {/* Telemetry replay dashboard for the study (the /dataset twin) */}
          <Route path="study/replay" element={<StudyReplayPage />} />
          {/* Invite links — unprotected, because redeeming the token IS the login.
              Static segments outrank :token, so the paths above are never shadowed. */}
          <Route path="study/:token" element={<StudyEntry mode="auto" />} />
          <Route path="study/:token/screening" element={<StudyEntry mode="screening" />} />
          <Route path="study/:token/main" element={<StudyEntry mode="main" />} />
          <Route path="study/:token/followup" element={<StudyEntry mode="followup" />} />
          {/* Coding tasks live at server-gated /chi/{questionNumber} */}
          <Route path="chi/:n" element={
            <StudyGate><StudyPage mode="main" /></StudyGate>
          } />
          {/* Any other path (React Router ranks a splat below every other route): see
              ReturnToKeptPage. */}
          <Route path="*" element={<ReturnToKeptPage />} />
        </Route>
      </Routes>
      <ToastContainer />
    </BrowserRouter>
  )
}

export default App
